import { useEffect, useState } from "react";
import {
  Box,
  Stack,
  TextField,
  MenuItem,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  IconButton,
  Chip,
  Switch,
  FormControlLabel,
  Tooltip,
} from "@mui/material";

import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import UnarchiveOutlinedIcon from "@mui/icons-material/UnarchiveOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";

import { useSnackbar } from "notistack";

import { useGlobalModals } from "../../contexts/GlobalModalsContext";
import PageHeader from "../../components/common/PageHeader";
import FilterBar from "../../components/common/FilterBar";
import DataTableWrapper from "../../components/common/DataTableWrapper";
import EmptyState from "../../components/common/EmptyState";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import FormModal from "../../components/common/FormModal";
import ViewModal from "../../components/common/ViewModal";

import { skipToken } from "@reduxjs/toolkit/query/react";
import {
  useListProvidersQuery,
  useGetProviderQuery,
  useCreateProviderMutation,
  useUpdateProviderMutation,
  useArchiveProviderMutation,
  useUnarchiveProviderMutation,
  useDeleteProviderMutation,
  useBulkDeleteArchivedProvidersMutation,
} from "../../features/masterdata/masterdataApi";

import type { Provider, ProviderCreate, ProviderType } from "../../features/masterdata/types";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { usePersistedListFilters } from "../../hooks/usePersistedListFilters";

const PROVIDER_TYPES: { value: ProviderType; label: string }[] = [
  { value: "bus_company", label: "Bus company" },
  { value: "taxi", label: "Taxi" },
  { value: "private_driver", label: "Private driver" },
  { value: "other", label: "Other" },
];

function typeLabel(t: string) {
  return PROVIDER_TYPES.find((x) => x.value === t)?.label ?? t;
}

type ProviderOrdering = "name" | "-name" | "created_at" | "-created_at";

export default function ProvidersPage() {
  const { enqueueSnackbar } = useSnackbar();
  const { showError, showConfirm } = useGlobalModals();

  const [filters, setFilters] = usePersistedListFilters("providers", {
    type: "" as ProviderType | "",
    isActive: "" as "" | "true" | "false",
    search: "",
    ordering: "name" as ProviderOrdering,
    includeArchived: false,
  });
  const { type, isActive, search, ordering, includeArchived } = filters;
  const debouncedSearch = useDebouncedValue(search);

  const [page, setPage] = useState(1);

  const toggleSort = (field: "name" | "created_at") => {
    setFilters((prev) => ({
      ...prev,
      ordering: (prev.ordering === field ? `-${field}` : prev.ordering === `-${field}` ? field : field) as ProviderOrdering,
    }));
  };
  const sortIcon = (field: "name" | "created_at") => {
    if (ordering === field) return " ↑";
    if (ordering === `-${field}`) return " ↓";
    return " ⇅";
  };
  const sortAria = (field: "name" | "created_at"): "ascending" | "descending" | "none" =>
    ordering === field ? "ascending" : ordering === `-${field}` ? "descending" : "none";

  // dialogs
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState<Provider | null>(null);

  const [openView, setOpenView] = useState(false);
  const [viewing, setViewing] = useState<Provider | null>(null);

  const [openDelete, setOpenDelete] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Provider | null>(null);

  const [form, setForm] = useState<ProviderCreate>({
    type: "bus_company",
    name: "",
    contact_name: null,
    email: null,
    phone: null,
    notes: null,
    is_active: true,
  });
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // reset page on filter changes
  useEffect(() => {
    setPage(1);
  }, [type, isActive, debouncedSearch, ordering, includeArchived]);

  const { data: editData, isLoading: isLoadingEdit } = useGetProviderQuery(
    editing ? editing.id : skipToken
  );

  const { data, isLoading, isFetching, error, refetch } = useListProvidersQuery({
    type: type || undefined,
    is_active: isActive === "" ? undefined : isActive === "true",
    search: debouncedSearch,
    ordering,
    page,
    include_archived: includeArchived || undefined,
  });

  const rows = data?.results ?? [];
  const count = data?.count ?? 0;
  const canPrev = !!data?.previous && page > 1;
  const canNext = !!data?.next;

  const [createProvider, { isLoading: isCreating }] = useCreateProviderMutation();
  const [updateProvider, { isLoading: isUpdating }] = useUpdateProviderMutation();
  const [archiveProvider, { isLoading: isArchiving }] = useArchiveProviderMutation();
  const [unarchiveProvider, { isLoading: isUnarchiving }] = useUnarchiveProviderMutation();
  const [deleteProvider, { isLoading: isDeleting }] = useDeleteProviderMutation();
  const [bulkDeleteArchivedProviders, { isLoading: isBulkDeleting }] = useBulkDeleteArchivedProvidersMutation();
  const [openBulkDelete, setOpenBulkDelete] = useState(false);

  const saving = isCreating || isUpdating;

  const showEmpty = !isLoading && !error && rows.length === 0;

  // When editing, sync form from GET-by-id so we have latest data
  useEffect(() => {
    if (editing?.id && editData?.id === editing.id) {
      setForm({
        type: editData.type,
        name: editData.name ?? "",
        contact_name: editData.contact_name ?? null,
        email: editData.email ?? null,
        phone: editData.phone ?? null,
        notes: editData.notes ?? null,
        is_active: !!editData.is_active,
      });
    }
  }, [editing?.id, editData]);

  // ------------------
  // Actions
  // ------------------
  const onOpenCreate = () => {
    setEditing(null);
    setSubmitAttempted(false);
    setForm({
      type: "bus_company",
      name: "",
      contact_name: null,
      email: null,
      phone: null,
      notes: null,
      is_active: true,
    });
    setOpenForm(true);
  };

  const onOpenEdit = (p: Provider) => {
    setSubmitAttempted(false);
    setEditing(p);
    setForm({
      type: p.type,
      name: p.name ?? "",
      contact_name: p.contact_name ?? null,
      email: p.email ?? null,
      phone: p.phone ?? null,
      notes: p.notes ?? null,
      is_active: !!p.is_active,
    });
    setOpenForm(true);
  };

  const onOpenView = (p: Provider) => {
    setViewing(p);
    setOpenView(true);
  };

  const onArchive = async (p: Provider) => {
    const confirmed = await showConfirm({
      title: "Archive provider?",
      message: `"${p.name}" will be archived and hidden from the default list.`,
      confirmText: "Archive",
      cancelText: "Cancel",
      tone: "danger",
    });
    if (!confirmed) return;
    try {
      await archiveProvider({ id: p.id }).unwrap();
      enqueueSnackbar("Provider archived", { variant: "success" });
    } catch (e: any) {
      const msg = e?.data?.detail || "Failed to archive provider";
      enqueueSnackbar(msg, { variant: "error" });
      showError({ title: "Archive failed", message: typeof msg === "string" ? msg : "Could not archive provider.", details: e?.data ? JSON.stringify(e.data, null, 2) : undefined });
    }
  };

  const onUnarchive = async (p: Provider) => {
    try {
      await unarchiveProvider({ id: p.id }).unwrap();
      enqueueSnackbar("Provider restored", { variant: "success" });
    } catch (e: any) {
      const msg = e?.data?.detail || "Failed to restore provider";
      enqueueSnackbar(msg, { variant: "error" });
      showError({ title: "Unarchive failed", message: typeof msg === "string" ? msg : "Could not restore provider.", details: e?.data ? JSON.stringify(e.data, null, 2) : undefined });
    }
  };

  const onSubmitForm = async () => {
    setSubmitAttempted(true);
    if (!form.name.trim()) return;
    try {
      const payload: ProviderCreate = {
        ...form,
        name: form.name.trim(),
        contact_name: form.contact_name || null,
        email: form.email || null,
        phone: form.phone || null,
        notes: form.notes || null,
        is_active: !!form.is_active,
      };

      if (!editing) {
        await createProvider(payload).unwrap();
        enqueueSnackbar("Provider created", { variant: "success" });
      } else {
        await updateProvider({ id: editing.id, patch: payload }).unwrap();
        enqueueSnackbar("Provider updated", { variant: "success" });
      }

      setOpenForm(false);
      setEditing(null);
      setSubmitAttempted(false);
      setPage(1);
    } catch (e: any) {
      const msg = e?.data?.detail || "Failed to save provider";
      enqueueSnackbar(msg, { variant: "error" });
      showError({ title: "Save failed", message: typeof msg === "string" ? msg : "Could not save provider.", details: e?.data ? JSON.stringify(e.data, null, 2) : undefined });
    }
  };

  const onConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteProvider({ id: deleteTarget.id }).unwrap();
      setOpenDelete(false);
      setDeleteTarget(null);
      enqueueSnackbar("Provider deleted", { variant: "success" });
    } catch (e: any) {
      const msg = e?.data?.detail || "Failed to delete provider";
      enqueueSnackbar(msg, { variant: "error" });
      showError({ title: "Delete failed", message: typeof msg === "string" ? msg : "Could not delete provider.", details: e?.data ? JSON.stringify(e.data, null, 2) : undefined });
    }
  };

  const onConfirmBulkDelete = async () => {
    try {
      const res = await bulkDeleteArchivedProviders().unwrap();
      setOpenBulkDelete(false);
      let msg = `Deleted ${res.deleted} archived provider(s)`;
      if (res.skipped > 0) msg += `. ${res.skipped} skipped (referenced).`;
      if (res.reason) msg += ` ${res.reason}`;
      enqueueSnackbar(msg, { variant: "success" });
    } catch (e: any) {
      const data = e?.data;
      const msg = data?.reason || data?.detail || "Failed to delete archived providers";
      enqueueSnackbar(msg, { variant: "error" });
      const details = data?.skippedIds?.length ? `Skipped IDs: ${data.skippedIds.join(", ")}` : (data ? JSON.stringify(data, null, 2) : undefined);
      showError({ title: "Bulk delete failed", message: typeof msg === "string" ? msg : "Could not delete archived providers.", details });
    }
  };

  return (
    <Box>
      <PageHeader
        title="Providers"
        subtitle="Bus companies, taxis, private drivers"
        actions={
          <Stack direction="row" spacing={1}>
            {includeArchived && (
              <Button
                variant="outlined"
                color="error"
                onClick={() => setOpenBulkDelete(true)}
                disabled={isBulkDeleting}
              >
                {isBulkDeleting ? "Deleting…" : "Bulk delete archived"}
              </Button>
            )}
            <Button variant="contained" startIcon={<AddOutlinedIcon />} onClick={onOpenCreate}>
              New Provider
            </Button>
          </Stack>
        }
      />

      <FilterBar loading={isLoading || isFetching}>
        <TextField
          label="Type"
          select
          value={type}
          onChange={(e) => setFilters({ type: e.target.value as ProviderType | "" })}
          size="small"
          sx={{ minWidth: { xs: 0, sm: 180 }, width: { xs: "100%", sm: "auto" } }}
        >
          <MenuItem value="">All</MenuItem>
          {PROVIDER_TYPES.map((t) => (
            <MenuItem key={t.value} value={t.value}>
              {t.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Active"
          select
          value={isActive}
          onChange={(e) => setFilters({ isActive: e.target.value as "" | "true" | "false" })}
          size="small"
          sx={{ minWidth: { xs: 0, sm: 130 }, width: { xs: "100%", sm: "auto" } }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="true">Active</MenuItem>
          <MenuItem value="false">Inactive</MenuItem>
        </TextField>
        <TextField
          label="Search (name, email, contact)"
          value={search}
          onChange={(e) => setFilters({ search: e.target.value })}
          size="small"
          placeholder="Search…"
          sx={{ minWidth: 0, flex: 1, width: { xs: "100%", sm: "auto" } }}
        />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={!!includeArchived}
              onChange={(e) => setFilters({ includeArchived: e.target.checked })}
            />
          }
          label="Archived only"
        />
      </FilterBar>

      {showEmpty ? (
        <EmptyState
          title="No providers yet"
          description="Create providers to assign transfers."
          actionLabel="Create provider"
          onAction={onOpenCreate}
        />
      ) : (
        <>
          <DataTableWrapper
            loading={isLoading}
            error={!!error}
            errorMessage="Failed to load providers."
            onRetry={() => refetch()}
            minWidth={560}
          >
            <Table sx={{ minWidth: 560 }}>
              <TableHead>
                <TableRow>
                  <TableCell aria-sort={sortAria("name")}>
                    <Box
                      component="span"
                      onClick={() => toggleSort("name")}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        cursor: "pointer",
                        fontWeight: 600,
                        "&:hover": { color: "primary.main" },
                      }}
                    >
                      Name{sortIcon("name")}
                    </Box>
                  </TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ fontWeight: 800 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {r.name}
                        {r.is_archived && <Chip size="small" label="Archived" color="default" variant="outlined" />}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={typeLabel(r.type)} />
                    </TableCell>
                    <TableCell>{r.contact_name || "-"}</TableCell>
                    <TableCell>{r.email || "-"}</TableCell>
                    <TableCell>{r.phone || "-"}</TableCell>
                    <TableCell>{r.is_active ? "Yes" : "No"}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => onOpenView(r)} aria-label="view">
                        <VisibilityOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => onOpenEdit(r)} aria-label="edit">
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                      {!r.is_archived && (
                        <Tooltip title="Archive">
                          <IconButton
                            size="small"
                            onClick={() => onArchive(r)}
                            disabled={isArchiving}
                            aria-label="Archive provider"
                          >
                            <ArchiveOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {r.is_archived && (
                        <>
                          <Tooltip title="Restore (unarchive)">
                            <IconButton
                              size="small"
                              onClick={() => onUnarchive(r)}
                              disabled={isUnarchiving}
                              aria-label="Unarchive provider"
                            >
                              <UnarchiveOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete permanently">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setDeleteTarget(r);
                                  setOpenDelete(true);
                                }}
                                disabled={isDeleting}
                                aria-label="Delete provider"
                                color="error"
                              >
                                <DeleteOutlineOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </DataTableWrapper>

          {/* paging */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            sx={{ mt: 2 }}
          >
            <Typography variant="body2" color="text.secondary">
              Total: {count} • Page: {page}
            </Typography>

            <Stack direction="row" spacing={1}>
              <Button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={isFetching || !canPrev}
              >
                Prev
              </Button>
              <Button onClick={() => setPage((p) => p + 1)} disabled={isFetching || !canNext}>
                Next
              </Button>
            </Stack>
          </Stack>
        </>
      )}

      <ViewModal
        open={openView}
        onClose={() => setOpenView(false)}
        title="Provider details"
        subtitle={
          viewing
            ? `${viewing.name} · ${typeLabel(viewing.type)} · ${viewing.is_active ? "Active" : "Inactive"}`
            : undefined
        }
        rows={
          viewing
            ? [
                { label: "Contact", value: viewing.contact_name },
                { label: "Email", value: viewing.email },
                { label: "Phone", value: viewing.phone },
                { label: "Notes", value: viewing.notes },
              ]
            : undefined
        }
        onEdit={
          viewing
            ? () => {
                onOpenEdit(viewing);
                setOpenView(false);
              }
            : undefined
        }
      />

      <FormModal
        open={openForm}
        onClose={() => { setSubmitAttempted(false); setOpenForm(false); }}
        title={editing ? "Edit provider" : "New provider"}
        onSubmit={onSubmitForm}
        submitDisabled={!form.name.trim()}
        loading={saving || (!!editing && isLoadingEdit)}
        maxWidth="sm"
      >
        <Stack spacing={2} mt={2}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Type"
              select
              value={form.type}
              onChange={(e) => setForm((p) => ({ ...p, type: e.target.value as ProviderType }))}
              fullWidth
            >
              {PROVIDER_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="Company or person name"
              required
              autoFocus
              fullWidth
              error={submitAttempted && !form.name.trim()}
              helperText={submitAttempted && !form.name.trim() ? "Name is required." : undefined}
            />
          </Stack>
          <TextField
            label="Contact name (optional)"
            value={form.contact_name ?? ""}
            onChange={(e) => setForm((p) => ({ ...p, contact_name: e.target.value || null }))}
            placeholder="Primary contact"
            fullWidth
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Email (optional)"
              type="email"
              value={form.email ?? ""}
              onChange={(e) => setForm((p) => ({ ...p, email: e.target.value || null }))}
              placeholder="email@example.com"
              fullWidth
            />
            <TextField
              label="Phone (optional)"
              value={form.phone ?? ""}
              onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value || null }))}
              placeholder="+30 …"
              fullWidth
            />
          </Stack>
          <TextField
            label="Notes (optional)"
            value={form.notes ?? ""}
            onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value || null }))}
            placeholder="Internal notes"
            multiline
            minRows={2}
            fullWidth
          />
          <FormControlLabel
            control={
              <Switch
                checked={!!form.is_active}
                onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
              />
            }
            label="Active"
          />
        </Stack>
      </FormModal>

      {/* Delete confirm */}
      <ConfirmDialog
        open={openDelete}
        title="Delete provider?"
        description={deleteTarget ? `This will permanently delete "${deleteTarget.name}".` : undefined}
        confirmText="Delete"
        severity="destructive"
        loading={isDeleting}
        onClose={() => {
          setOpenDelete(false);
          setDeleteTarget(null);
        }}
        onConfirm={onConfirmDelete}
      />
      <ConfirmDialog
        open={openBulkDelete}
        title="Delete all archived providers?"
        description="This will permanently delete all archived providers. This cannot be undone."
        confirmText="Delete all"
        severity="destructive"
        loading={isBulkDeleting}
        onClose={() => setOpenBulkDelete(false)}
        onConfirm={onConfirmBulkDelete}
      />
    </Box>
  );
}
